import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

export interface SelectOptions<T> {
  comparatorFn?: (previous: T, current: T) => boolean;
}

export class StateService<T> {
  private state$: BehaviorSubject<T>;

  constructor(initialState: T) {
    this.state$ = new BehaviorSubject<T>(initialState);
  }

  protected get state(): T {
    return this.state$.getValue();
  }

  /**
   * Selects a slice of the current state and returns it as an Observable.
   * The slice is determined by the provided mapping function `mapFn`.
   *
   * @template K The type of the slice of the state to be selected.
   * @param mapFn A function that takes the current state and returns the desired slice.
   * @param options Optional settings for comparison logic.
   * @returns An Observable of the selected slice of the state.
   *
   * @example
   * // Suppose the state is of type { count: number, items: string[] }
   *
   * this.select(state => state.count)
   *   .subscribe(count => {
   *     console.log(`The current count is ${count}`);
   *   });
   *
   * @protected
   */
  protected select<K>(mapFn: (state: T) => K, options?: SelectOptions<K>): Observable<K> {
    return this.state$.asObservable().pipe(
      map((state: T) => mapFn(state)),
      distinctUntilChanged(options?.comparatorFn),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      })
    );
  }

  /**
   * Sets the new state by performing a shallow copy of the new state object.
   * Be cautious when manipulating nested objects or arrays, as they are not deeply copied.
   *
   * @param newState The new partial state to be merged into the current state.
   * @protected
   */
  protected setState(newState: Partial<T>): void {
    this.state$.next({
      ...this.state,
      ...newState,
    });
  }
}
